import { Inject, Injectable } from '@angular/core';

import { FUSUS_GREEN } from '@lib/common/constants';
import { IMarker as _IMarker } from '@lib/geo/map';
import { SnBadgeConfig } from '@lib/ui/badge';

import { EsriMap } from '../../../map';
import { EsriModules, ESRI_MODULES_TOKEN } from '../../../tokens';
import { IEsriMarker } from '../interfaces/esri-marker.interface';

interface MapViewWithGraphics {
  mapView?: {
    graphics?: {
      removeMany: (graphics: unknown[]) => void;
      addMany: (graphics: unknown[]) => void;
    };
  };
}

type IMarker = _IMarker<EsriMap>;

/**
 * Service responsible for badge management operations on ESRI Map markers.
 * Handles creation, styling, and positioning of various badge types using ESRI Graphics.
 */
@Injectable()
export class EsriMarkerBadgeService {
  constructor(@Inject(ESRI_MODULES_TOKEN) private readonly esri: EsriModules) {}
  public setBadge(esriMarker: IEsriMarker, marker: IMarker, badge: SnBadgeConfig): void {
    esriMarker.setBadge(badge);
    marker.badge = badge;
  }

  public getBadge(marker: IMarker): SnBadgeConfig {
    return marker.badge;
  }

  public createBadge(esriMarker: IEsriMarker, marker: IMarker, badgeClass?: string): void {
    const badgeType = this._determineBadgeType(marker, badgeClass);

    switch (badgeType) {
      case 'cluster':
        this._createClusterBadge(esriMarker, marker);
        break;
      case 'status':
        this._createStatusBadge(esriMarker, marker);
        break;
      case 'selected':
        this._createSelectedBadge(esriMarker, marker);
        break;
      case 'icon':
        this._createIconBadge(esriMarker, marker);
        break;
      case 'custom':
        this._createCustomBadge(esriMarker, marker, badgeClass);
        break;
      case 'standard':
      default:
        this._createStandardBadge(esriMarker, marker);
        break;
    }
  }

  public createSelectedBadge(esriMarker: IEsriMarker, marker: IMarker): void {
    this._createSelectedBadge(esriMarker, marker);
    marker.selected = true;
  }

  public removeSelectedBadge(esriMarker: IEsriMarker, marker: IMarker): void {
    this._removeSelectedBadge(esriMarker);
    marker.selected = false;
  }

  public setBadgeStatus(
    esriMarker: IEsriMarker,
    marker: IMarker,
    status: string,
    badgePosition: Record<string, unknown>,
  ): void {
    this._createStatusBadge(esriMarker, marker, status, badgePosition);
  }

  private _determineBadgeType(marker: IMarker, badgeClass?: string): string {
    if (marker.metadata?.entityType === 'cluster') {
      return 'cluster';
    }

    if (marker.selected) {
      return 'selected';
    }

    if (badgeClass) {
      if (badgeClass.includes('status')) return 'status';
      if (badgeClass.includes('icon')) return 'icon';
      if (badgeClass.includes('custom')) return 'custom';
    }

    if (marker.badge) {
      return 'standard';
    }

    return 'standard';
  }

  private _createClusterBadge(esriMarker: IEsriMarker, marker: IMarker): void {
    const objectCount = marker.metadata?.customData?.clusterObjects || 0;
    const geometry = this._getMarkerGeometry(esriMarker);

    const badgeBackgroundSymbol = new this.esri.SimpleMarkerSymbol({
      style: 'circle',
      color: 'rgb(255, 255, 255, 0.7)',
      size: this._calculateBadgeSize(objectCount.toString()),
      outline: {
        color: [255, 255, 255],
        width: 2,
      },
      xoffset: 12,
      yoffset: 12,
    });

    const badgeSymbol = new this.esri.TextSymbol({
      text: objectCount.toString(),
      color: '#000',
      font: {
        size: 7,
        weight: 'lighter',
      },
      horizontalAlignment: 'center',
      verticalAlignment: 'middle',
      xoffset: 12,
      yoffset: 12,
    });

    const badgeBackgroundGraphic = new this.esri.Graphic({
      geometry,
      symbol: badgeBackgroundSymbol,
    });

    const badgeGraphic = new this.esri.Graphic({
      geometry,
      symbol: badgeSymbol,
    });

    this._addBadgeGraphics(esriMarker, [badgeBackgroundGraphic, badgeGraphic]);
  }

  private _createStatusBadge(
    esriMarker: IEsriMarker,
    marker: IMarker,
    status?: string,
    position?: Record<string, unknown>,
  ): void {
    const geometry = this._getMarkerGeometry(esriMarker);
    const xoffset = (position?.xoffset as number) || 11;
    const yoffset = (position?.yoffset as number) || -11;

    const badgeBackgroundSymbol = new this.esri.SimpleMarkerSymbol({
      style: 'circle',
      color: status || '#ff0000',
      size: 8,
      outline: {
        color: '#000',
        width: 0.5,
      },
      xoffset,
      yoffset,
    });

    const badgeBackgroundGraphic = new this.esri.Graphic({
      geometry,
      symbol: badgeBackgroundSymbol,
    });

    this._addBadgeGraphics(esriMarker, [badgeBackgroundGraphic]);
  }

  private _createSelectedBadge(esriMarker: IEsriMarker, marker: IMarker): void {
    const geometry = this._getMarkerGeometry(esriMarker);

    const badgeBackgroundSymbol = new this.esri.SimpleMarkerSymbol({
      style: 'circle',
      color: FUSUS_GREEN,
      size: 12,
      outline: {
        color: FUSUS_GREEN,
      },
      xoffset: 12,
      yoffset: 8,
    });

    const badgeSymbol = new this.esri.TextSymbol({
      text: 'âœ"',
      color: '#fff',
      font: {
        size: 7,
        weight: 'lighter',
      },
      horizontalAlignment: 'center',
      verticalAlignment: 'middle',
      xoffset: 12,
      yoffset: 8,
    });

    const badgeBackgroundGraphic = new this.esri.Graphic({
      geometry,
      symbol: badgeBackgroundSymbol,
    });

    const badgeGraphic = new this.esri.Graphic({
      geometry,
      symbol: badgeSymbol,
    });

    this._addBadgeGraphics(esriMarker, [badgeBackgroundGraphic, badgeGraphic]);
  }

  private _createIconBadge(esriMarker: IEsriMarker, marker: IMarker): void {
    const geometry = this._getMarkerGeometry(esriMarker);
    const iconCode = marker.iconLabel?.text || '📍';

    const badgeSymbol = new this.esri.TextSymbol({
      text: iconCode,
      color: '#000',
      font: {
        size: 10,
        weight: 'normal',
      },
      horizontalAlignment: 'center',
      verticalAlignment: 'middle',
      xoffset: 15,
      yoffset: -15,
    });

    const badgeGraphic = new this.esri.Graphic({
      geometry,
      symbol: badgeSymbol,
    });

    this._addBadgeGraphics(esriMarker, [badgeGraphic]);
  }

  private _createCustomBadge(esriMarker: IEsriMarker, marker: IMarker, badgeClass?: string): void {
    const geometry = this._getMarkerGeometry(esriMarker);
    const badgeText = marker.title || 'Custom';

    const badgeBackgroundSymbol = new this.esri.SimpleMarkerSymbol({
      style: 'circle',
      color: 'rgb(255, 255, 255, 0.9)',
      size: this._calculateBadgeSize(badgeText),
      outline: {
        color: '#000',
        width: 1,
      },
      xoffset: 12,
      yoffset: 12,
    });

    const badgeSymbol = new this.esri.TextSymbol({
      text: badgeText,
      color: '#000',
      font: {
        size: 8,
        weight: 'normal',
      },
      horizontalAlignment: 'center',
      verticalAlignment: 'middle',
      xoffset: 12,
      yoffset: 12,
    });

    const badgeBackgroundGraphic = new this.esri.Graphic({
      geometry,
      symbol: badgeBackgroundSymbol,
    });

    const badgeGraphic = new this.esri.Graphic({
      geometry,
      symbol: badgeSymbol,
    });

    this._addBadgeGraphics(esriMarker, [badgeBackgroundGraphic, badgeGraphic]);
  }

  private _createStandardBadge(esriMarker: IEsriMarker, marker: IMarker): void {
    if (marker.badge) {
      esriMarker.setBadge(marker.badge);
    }
  }

  private _removeSelectedBadge(esriMarker: IEsriMarker): void {
    const marker = esriMarker.getMarker();

    const mapView = (esriMarker as unknown as MapViewWithGraphics).mapView;
    if (mapView?.graphics && marker.attributes?.badgeGraphics) {
      mapView.graphics.removeMany(marker.attributes.badgeGraphics);
    }

    if (marker.attributes) {
      marker.attributes.badgeGraphics = [];
    }
  }

  private _getMarkerGeometry(esriMarker: IEsriMarker): unknown {
    const marker = esriMarker.getMarker();
    return marker.geometry;
  }

  private _addBadgeGraphics(esriMarker: IEsriMarker, graphics: unknown[]): void {
    const marker = esriMarker.getMarker();

    if (!marker.attributes) {
      marker.attributes = {};
    }
    if (!marker.attributes.badgeGraphics) {
      marker.attributes.badgeGraphics = [];
    }
    marker.attributes.badgeGraphics.push(...graphics);

    const mapView = (esriMarker as unknown as MapViewWithGraphics).mapView;
    if (mapView?.graphics) {
      mapView.graphics.addMany(graphics);
    }
  }

  private _calculateBadgeSize(text: string): number {
    const baseSize = 20;
    const textLength = text.length;
    return Math.max(baseSize, textLength * 8);
  }
}
